// Verdict-grouped scorecard table. Rows come pre-joined with the action columns
// from ScorecardService.getLatestScorecard() (snake_case DB column names).
// Filtering (MP-relevant toggle, verdict multi-select, text search) and grouping
// happen here; WarRoom.js only owns the filter *state*.
import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import ActionCell from './ActionCell';
import AsinHistoryPanel from './AsinHistoryPanel';
import ApiClient from '../../utils/ApiClient';

export const VERDICT_GROUPS = [
    {
        verdict: 'ENFORCE-MAP',
        definition: 'A competitor is selling below MAP — enforce before any growth spend.',
    },
    {
        verdict: 'DEFEND',
        definition: "We win the box but runway or FC spread is thin, or we're out of stock in most checks we lose — restock and replenish first.",
    },
    {
        verdict: 'CONTEST',
        definition: 'Tied with the winner — ties go to them; must become strictly faster.',
    },
    {
        verdict: 'PRICE',
        definition: "The winner is cheaper in most checks we lose — reprice or accept; don't add stock.",
    },
    {
        verdict: 'FLIP',
        definition: 'MP holds the box with thin inventory on a short tail — cheap to flip with a small shipment.',
    },
    {
        verdict: 'SPREAD',
        definition: 'Slower than the winner in most lost checks — place stock nearer; check depth before buying.',
    },
    {
        verdict: 'HOLD',
        definition: "We're winning with healthy runway and FC spread — no action needed.",
    },
    {
        verdict: 'REVIEW',
        definition: 'Offer missing despite stock, unclassified loss, or missing data — investigate.',
    },
];

const VERDICT_HEADER_STYLES = {
    'ENFORCE-MAP': 'bg-amber-50 border-amber-300 text-amber-900',
    'DEFEND': 'bg-red-50 border-red-300 text-red-900',
    'CONTEST': 'bg-blue-50 border-blue-300 text-blue-900',
    'PRICE': 'bg-rose-50 border-rose-300 text-rose-900',
    'FLIP': 'bg-green-50 border-green-300 text-green-900',
    'SPREAD': 'bg-purple-50 border-purple-300 text-purple-900',
    'HOLD': 'bg-gray-50 border-gray-300 text-gray-900',
    'REVIEW': 'bg-slate-50 border-slate-300 text-slate-900',
};

const TIER_BADGE = {
    Standard: 'bg-green-100 text-green-800',
    Oversize: 'bg-amber-100 text-amber-800',
    ExtraLarge: 'bg-red-100 text-red-800',
};

const ALL_VERDICTS = VERDICT_GROUPS.map((g) => g.verdict);

function mpRelevant(row) {
    return row.mp_units_today != null || row.natl_holder_now === 'MP' || (row.pins_mp || 0) > 0;
}

function ageDays(createdAt) {
    if (!createdAt) return null;
    const then = new Date(createdAt).getTime();
    if (Number.isNaN(then)) return null;
    return Math.max(0, Math.floor((Date.now() - then) / 86400000));
}

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const bbColor = (v) => {
    if (v == null) return '';
    if (v >= 70) return 'text-green-700';
    if (v >= 30) return 'text-amber-600';
    return 'text-red-600';
};

const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `$${Math.round(v).toLocaleString()}`);

function ScorecardTable({
    rows,
    filterMpOnly,
    onToggleMpOnly,
    verdictFilter,
    onToggleVerdict,
    search,
    onSearchChange,
    onActionSaved,
}) {
    const [expanded, setExpanded] = useState(new Set());
    const [historyByAsin, setHistoryByAsin] = useState({});

    const toggleExpand = (asin) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(asin)) {
                next.delete(asin);
            } else {
                next.add(asin);
            }
            return next;
        });

        if (!historyByAsin[asin]) {
            setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'loading', rows: [] } }));
            ApiClient.get(`/api/war-room/history/${asin}`)
                .then((response) => {
                    setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'loaded', rows: response.data || [] } }));
                })
                .catch((err) => {
                    console.error('Error fetching ASIN history:', err);
                    setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'error', rows: [] } }));
                });
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (rows || []).filter((r) => {
            if (filterMpOnly && !mpRelevant(r)) return false;
            if (!verdictFilter.has(r.verdict)) return false;
            if (q) {
                const hitAsin = (r.asin || '').toLowerCase().includes(q);
                const hitTitle = (r.title || '').toLowerCase().includes(q);
                const hitSku = (r.sku || '').toLowerCase().includes(q);
                if (!hitAsin && !hitTitle && !hitSku) return false;
            }
            return true;
        });
    }, [rows, filterMpOnly, verdictFilter, search]);

    const grouped = useMemo(() => {
        const byVerdict = {};
        ALL_VERDICTS.forEach((v) => { byVerdict[v] = []; });
        filtered.forEach((row) => {
            if (byVerdict[row.verdict]) byVerdict[row.verdict].push(row);
        });
        ALL_VERDICTS.forEach((v) => {
            byVerdict[v].sort((a, b) => (b.monthly_net_pool || 0) - (a.monthly_net_pool || 0));
        });
        return byVerdict;
    }, [filtered]);

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200">
            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-4 p-4 border-b border-gray-200">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={filterMpOnly}
                        onChange={onToggleMpOnly}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    MP-relevant only
                </label>

                <div className="flex flex-wrap items-center gap-3">
                    {ALL_VERDICTS.map((v) => (
                        <label key={v} className="inline-flex items-center gap-1 text-xs text-gray-600">
                            <input
                                type="checkbox"
                                checked={verdictFilter.has(v)}
                                onChange={() => onToggleVerdict(v)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {v}
                        </label>
                    ))}
                </div>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search ASIN or title…"
                    className="ml-auto w-64 text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            {VERDICT_GROUPS.map(({ verdict, definition }) => {
                if (!verdictFilter.has(verdict)) return null;
                const groupRows = grouped[verdict] || [];
                if (groupRows.length === 0) return null;
                const groupPool = groupRows.reduce((sum, r) => sum + (r.monthly_net_pool || 0), 0);

                return (
                    <div key={verdict} className="border-b border-gray-200 last:border-b-0">
                        <div className={`px-4 py-2 border-b flex flex-wrap items-baseline gap-2 ${VERDICT_HEADER_STYLES[verdict]}`}>
                            <span className="font-bold text-sm">{verdict}</span>
                            <span className="text-xs opacity-80">{definition}</span>
                            <span className="ml-auto text-xs font-semibold whitespace-nowrap">
                                {groupRows.length} ASINs &middot; {fmtMoney(groupPool)}/mo
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        <th className="px-1.5 py-1.5 w-8"></th>
                                        <th className="px-1.5 py-1.5">ASIN</th>
                                        <th className="px-1.5 py-1.5">Title</th>
                                        <th className="px-1.5 py-1.5">Tier</th>
                                        <th className="px-1.5 py-1.5">Reason</th>
                                        <th className="px-1.5 py-1.5 text-right">Units</th>
                                        <th className="px-1.5 py-1.5 text-right">Sold</th>
                                        <th className="px-1.5 py-1.5 text-right">BB%</th>
                                        <th className="px-1.5 py-1.5 text-right">FCs</th>
                                        <th className="px-1.5 py-1.5 text-right">Rwy</th>
                                        <th className="px-1.5 py-1.5 text-right">MP</th>
                                        <th className="px-1.5 py-1.5 text-right">MPmax</th>
                                        <th className="px-1.5 py-1.5 text-right">MP5wk</th>
                                        <th className="px-1.5 py-1.5 text-right">Pool</th>
                                        <th className="px-1.5 py-1.5">Undercut</th>
                                        <th className="px-1.5 py-1.5 text-right">Flip</th>
                                        <th className="px-1.5 py-1.5">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-xs">
                                    {groupRows.map((row) => {
                                        const isExpanded = expanded.has(row.asin);
                                        const history = historyByAsin[row.asin];
                                        return (
                                        <React.Fragment key={row.asin}>
                                        <tr className="align-top hover:bg-gray-50">
                                            <td className="px-1.5 py-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleExpand(row.asin)}
                                                    aria-label={isExpanded ? 'Collapse weekly history' : 'Expand weekly history'}
                                                    className="text-gray-400 hover:text-gray-700"
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDownIcon className="h-4 w-4" />
                                                    ) : (
                                                        <ChevronRightIcon className="h-4 w-4" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-1.5 py-1.5 whitespace-nowrap">
                                                <a
                                                    href={`https://www.amazon.com/dp/${row.asin}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                                                >
                                                    {row.asin}
                                                </a>
                                            </td>
                                            <td className="px-1.5 py-1.5 max-w-[11rem] truncate text-xs" title={row.title || ''}>
                                                {row.title || '—'}
                                            </td>
                                            <td className="px-1.5 py-1.5">
                                                {row.size_tier ? (
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TIER_BADGE[row.size_tier] || 'bg-gray-100 text-gray-800'}`}>
                                                        {row.size_tier}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="px-1.5 py-1.5 text-xs text-gray-700 max-w-[13rem] whitespace-normal">
                                                {row.verdict_reason || '—'}
                                            </td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.our_units)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.our_sold_28d)}</td>
                                            <td className={`px-1.5 py-1.5 text-right font-semibold ${bbColor(row.pv_bb_pct_28d)}`}>{fmtPct(row.pv_bb_pct_28d)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.fc_count)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.runway_days)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.mp_units_today)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtNum(row.mp_max_90d)}</td>
                                            <td className="px-1.5 py-1.5 text-right">{fmtPct(row.mp_natl_5wk_pct)}</td>
                                            <td className="px-1.5 py-1.5 text-right font-semibold">{fmtMoney(row.monthly_net_pool)}</td>
                                            <td className="px-1.5 py-1.5 whitespace-nowrap">
                                                {row.breaker_pct_under != null ? (
                                                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                        ⚠ {fmtPct(row.breaker_pct_under)}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="px-1.5 py-1.5 text-right">
                                                {row.flip_inv != null ? `${row.flip_inv} / ${row.flip_fcs}fc` : '—'}
                                            </td>
                                            <td className="px-1.5 py-1.5">
                                                <ActionCell
                                                    asin={row.asin}
                                                    actionStatus={row.action_status}
                                                    actionNote={row.action_note}
                                                    actionAge={ageDays(row.action_created_at)}
                                                    onSaved={onActionSaved}
                                                />
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={17} className="px-1.5 py-1.5 bg-gray-50">
                                                    <AsinHistoryPanel
                                                        status={history ? history.status : 'loading'}
                                                        rows={history ? history.rows : []}
                                                        asin={row.asin}
                                                        dwCov1dPct={row.dw_cov_1d_pct}
                                                        dwCov2dPct={row.dw_cov_2d_pct}
                                                        dwCov1dEffPct={row.dw_cov_1d_eff_pct}
                                                        dwCov2dEffPct={row.dw_cov_2d_eff_pct}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}

            {filtered.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">No ASINs match the current filters.</div>
            )}
        </div>
    );
}

export default ScorecardTable;
